import Swal from "sweetalert2";
import type { TFunction } from "i18next";
import {
  fetchHikvisionDevices,
  controlHikvisionDeviceSmart,
} from "../../store/oasis/hikvisionAccessStore";
import { doorForDirection } from "./accessSection/doorForDirection";
import { showDoorWaitingToast } from "./doorWaitingToast";

/**
 * Levanta la pluma de un patio en la dirección dada y avisa el resultado.
 *
 * Era el final de `handleNewAccess` (alta manual en Accesos). Sale aparte para
 * que "DAR SALIDA MANUAL" del modal de inspección levante la MISMA pluma por el
 * mismo camino: dispositivos del patio por `door_type`, puerta por el mapa de
 * lectoras (doorForDirection — en Acuapue la salida es la puerta 1, la entrada
 * la 2), reintento mientras el controlador esté ocupado (doorBusyRetry) y
 * nada que mostrar si el operador ya se fue (`signal`).
 *
 * Nunca lanza: una pluma que no abre no deshace el evento que ya se registró.
 */
export async function openYardGate(opts: {
  yardId: string;
  direction: "entry" | "exit";
  t: TFunction;
  signal?: AbortSignal;
}): Promise<void> {
  const { yardId, direction, t, signal } = opts;
  try {
    const { data: devices } = await fetchHikvisionDevices({ yard_id: yardId, limit: 50, page: 1 });
    const matching = devices.filter((d) =>
      direction === "entry"
        ? d.door_type === "entry" || d.door_type === "bidirectional"
        : d.door_type === "exit" || d.door_type === "bidirectional",
    );
    if (matching.length === 0) {
      Swal.fire({
        icon: "info",
        title: t("access.messages.noDeviceTitle"),
        text: direction === "entry"
          ? t("access.messages.noDeviceEntryText")
          : t("access.messages.noDeviceExitText"),
        timer: 3500,
        showConfirmButton: true,
        confirmButtonText: t("access.details.close"),
      });
      return;
    }
    const results = await Promise.all(
      matching.map((d) =>
        controlHikvisionDeviceSmart(
          { id: d.id, connection_mode: d.connection_mode },
          "open",
          {
            door_no: doorForDirection(d, direction),
            onBusyRetry: (attempt) => showDoorWaitingToast(t, attempt),
            signal,
          },
        ).catch((err) => ({
          success: false,
          action: "open" as const,
          error: err instanceof Error ? err.message : String(err),
        })),
      ),
    );
    // Salió de la pantalla a mitad de los reintentos: Swal es global y el
    // resultado aparecería en otra página.
    if (signal?.aborted) return;
    const anyOk = results.some((r) => r.success);
    const allOk = results.every((r) => r.success);
    if (allOk) {
      Swal.fire({ icon: "success", title: t("access.messages.doorOpenedTitle"), timer: 1800, showConfirmButton: false });
    } else if (anyOk) {
      Swal.fire({
        icon: "warning",
        title: t("access.messages.doorPartialTitle"),
        text: t("access.messages.doorPartialText"),
        timer: 3000,
        showConfirmButton: false,
      });
    } else {
      const firstError = results.find((r) => !r.success)?.error;
      Swal.fire({
        icon: "error",
        title: t("access.messages.doorFailedTitle"),
        text: firstError || t("access.messages.doorFailedText"),
      });
    }
  } catch (err: unknown) {
    if (signal?.aborted) return;
    Swal.fire({
      icon: "error",
      title: t("access.messages.doorErrorTitle"),
      text: err instanceof Error ? err.message : t("access.messages.unknownError"),
    });
  }
}
